import { QuestConfig, QuestsApi, Token, GetAllQuestConfigsForUserSort, SortOrder } from "@devour/client";
import getConfig from "@/utils/getConfig";
import { useQuery, keepPreviousData } from "@tanstack/react-query";

export const USE_GET_ALL_QUEST_CONFIGS_FOR_USER_KEY = "quest-configs-user-all";

interface GetAllQuestConfigsForUserRequest {
    sort?: GetAllQuestConfigsForUserSort;
    order?: SortOrder;
    limit?: number;
    offset?: number;
}

async function fetchAllQuestConfigsForUser(
    fullToken: Token,
    query: GetAllQuestConfigsForUserRequest = {},
): Promise<Array<QuestConfig>> {
    const data = await new QuestsApi(getConfig(fullToken)).getQuestConfigsUserAll({
        limit: query.limit || 50,
        offset: query.offset || 0,
        sort: query.sort,
        order: query.order,
    });
    return data.questConfigs;
}

function getAllQuestConfigsForUserQuery(fullToken: Token, query: GetAllQuestConfigsForUserRequest = {}) {
    return {
        queryKey: [
            USE_GET_ALL_QUEST_CONFIGS_FOR_USER_KEY,
            fullToken?.id ?? "unauthenticated",
            query,
        ],
        queryFn: () => fetchAllQuestConfigsForUser(fullToken, query),
        // keepPreviousData → v5 API: show previous page data while next page loads (no flash)
        placeholderData: keepPreviousData,
        /*
         * 30-day staleTime is safe because quest configs are invalidated via WebSocket
         * (INVALIDATE_QUEST_CONFIGS event) whenever an admin publishes or modifies a quest.
         */
        staleTime: 30 * 24 * 60 * 60000,
        refetchOnWindowFocus: false,
    } as const;
}

export function useGetAllQuestConfigsForUser(fullToken: Token, query?: GetAllQuestConfigsForUserRequest) {
    const baseQuery = getAllQuestConfigsForUserQuery(fullToken, query || {});
    const enabled = Boolean(fullToken?.token && query !== undefined);

    return useQuery({
        ...baseQuery,
        enabled,
    });
}
